import { Suspense } from 'react';
import { ErrorBoundary } from 'react-error-boundary';
import { Navigate, Outlet, redirect, Route } from 'react-router';
import {
  CertificateAuthoritiesRoot,
  ContactUsPage,
  DnsPreviewRoot,
  DownloadsRoot,
  EndpointUsersRoot,
  GetStartedPage,
} from '#route-bundles/authenticated.tsx';
import { DeletedAccountPage, SignupPage } from '#route-bundles/unauthenticated.tsx';
import { OopsPageError } from '#screens/errors/OopsPageError.tsx';
import { Devtools } from './components/patterns/devtools/Devtools.tsx';
import { NavLayout } from './components/patterns/layouts/nav/NavLayout.tsx';
import { Authenticated } from './components/patterns/routing/Authenticated.tsx';
import { FeatureFlagged } from './components/patterns/routing/FeatureFlagged.tsx';
import { GotStarted } from './components/patterns/routing/GotStarted.tsx';
import { useGlobalState } from './global/context/global-state/useGlobalState.tsx';
import { NotFound } from './screens/404/NotFound.tsx';
import { authRootRoute } from './screens/auth/AuthRoot.tsx';
import { Oops } from './screens/errors/Oops.tsx';
import { hostsRootRoute } from './screens/hosts/HostsRoot.tsx';
import { legacyHostsLoader } from './screens/hosts/legacyHostsRedirect.ts';
import { LoadingAuthPage } from './screens/loading/LoadingAuthPage.tsx';
import { logsRootRoute } from './screens/logs/LogsRoot.tsx';
import { rolesRootRoute } from './screens/roles/RolesRoot.tsx';
import { routesRootRoute } from './screens/routes/RoutesRoot.tsx';
import { settingsRootRoute } from './screens/settings/SettingsRoot.tsx';
import { tagsRootRoute } from './screens/tags/TagsRoot.tsx';
import { Theme } from './Theme.tsx';
import { Titled } from './util/title.ts';

function App() {
  return (
    <Titled title={() => 'Defined Networking'}>
      <Theme />
      <Outlet />
      <Devtools />
      <div id="scrollbar-test" />
    </Titled>
  );
}

function NavLayoutWrapper() {
  const { setGlobalError } = useGlobalState();

  return (
    <Suspense>
      <NavLayout>
        <ErrorBoundary
          fallbackRender={(props) => (
            // `resetErrorBoundary` is deliberately withheld: remounting re-throws the cached query
            // error, since React Query sets `retryOnMount = false` inside a boundary. Without it,
            // Refresh reloads the page, which is the only thing that actually clears this state.
            // (Wiring up `useQueryErrorResetBoundary` + onReset was considered: it only unlocks the
            // refetch when `reset()` is called, but `fullPageloadNeeded` derives from `globalError`,
            // links are forced to full reloads below, and nothing clears the boundary on the next
            // route render — so in-place recovery still needs a coordinating redesign rather than
            // this callback alone. Reload is honest about what works today.)
            // Don't want to show `error` w/o a filter
            <Oops error={props.error as unknown} showErrorDetails={false} />
          )}
          onError={(error) => {
            // Force links to be full page loads, to reset the error boundary
            setGlobalError(error);
          }}
        >
          <Outlet />
        </ErrorBoundary>
      </NavLayout>
    </Suspense>
  );
}

export const appRoute = (
  <Route element={<App />} errorElement={<OopsPageError />} HydrateFallback={() => null}>
    <Route path="/auth">{authRootRoute}</Route>
    <Route path="/signup" element={<SignupPage />} />
    <Route path="/deleted-account" element={<DeletedAccountPage />} />
    <Route
      element={
        <Authenticated>
          <Outlet />
        </Authenticated>
      }
    >
      <Route
        path="/signup/pro"
        element={
          <Suspense fallback={<LoadingAuthPage />}>
            <Outlet />
          </Suspense>
        }
      >
        <Route
          index={true}
          // Lazy load this so that we don't pollute every page with calls to stripe
          lazy={() =>
            import('#screens/signup/pro/SignupCheckoutPage.tsx').then(({ SignupCheckoutPage }) => ({
              Component: SignupCheckoutPage,
            }))
          }
        />
      </Route>
      <Route path="/get-started" element={<GetStartedPage />} />
      <Route element={<NavLayoutWrapper />}>
        <Route
          element={
            <GotStarted>
              <Outlet />
            </GotStarted>
          }
        >
          <Route
            index={true}
            loader={() => redirect('/hosts')}
            element={<Navigate to={{ pathname: '/hosts' }} replace={true} />}
          />
          <Route path="/hosts">{hostsRootRoute}</Route>
          {/* The standalone lighthouse/relay pages merged into /hosts; their URLs redirect
              indefinitely (bookmarks, docs, and support links still point here). */}
          <Route path="/lighthouses/*" loader={legacyHostsLoader('lighthouse')} />
          <Route path="/relays/*" loader={legacyHostsLoader('relay')} />
          <Route path="/roles">{rolesRootRoute}</Route>
          <Route path="/settings">{settingsRootRoute}</Route>
          <Route path="/tags">{tagsRootRoute}</Route>
          <Route path="/routes">{routesRootRoute}</Route>
          <Route path="/users" element={<EndpointUsersRoot />} />
          <Route path="/downloads" element={<DownloadsRoot />} />
        </Route>
        <Route
          path="/certificate-authorities"
          element={
            <FeatureFlagged featureFlagIdent="LIST_CERT_AUTHORITIES">
              <CertificateAuthoritiesRoot />
            </FeatureFlagged>
          }
        />
        <Route
          path="/dns-preview"
          element={
            <FeatureFlagged featureFlagIdent="MANAGED_DNS">
              <DnsPreviewRoot />
            </FeatureFlagged>
          }
        />
        <Route path="/logs">{logsRootRoute}</Route>
        {/* Deliberately outside the GotStarted gate: a zero-network user mid-onboarding is exactly
            who needs to reach support, so this page must not bounce them back to /get-started. */}
        <Route path="/support/contact" element={<ContactUsPage />} />
        <Route path="*" element={<NotFound />} />
      </Route>
    </Route>
  </Route>
);
